import { useState, useMemo } from 'react';
import { cn } from '@/lib/utils';
import { Tooltip } from '@/components/ui/Tooltip';
import {
  Loader2, ChevronDown, ChevronRight, CheckCircle2, FileText,
  Zap, Expand, Minimize2,
} from 'lucide-react';
import { MarkdownContent } from '@/components/verdict/MarkdownContent';
import { getProviderFromModel as detectProvider, getProviderLogo } from '@/utils/providerUtils';
import { WORKSTREAM_COLORS } from './constants';
import { StrategicAgentModal } from './StrategicAgentModal';
import type { StrategicWorkstream, StrategicAgent, StrategicPhase } from '@/hooks/useStrategicStream';

// ============================================================================
// STRATEGIC AGENT NODE COMPACT
// ============================================================================

function StrategicAgentNodeCompact({
  agent,
  agentIndex,
  workstreamColor,
  isActive,
  onClick,
}: {
  agent: StrategicAgent;
  agentIndex: number;
  workstreamColor: string;
  isActive: boolean;
  onClick?: () => void;
  isSmallScreen?: boolean;
}) {
  const provider = detectProvider(agent.model || '');

  const displayName = useMemo(() => {
    const role = agent.agentRole || `Agent ${agentIndex + 1}`;
    const modelName = agent.model || '';
    if (modelName) {
      return `${role} (${modelName})`;
    }
    return role;
  }, [agent.agentRole, agent.model, agentIndex]);

  const hasOutput = !!agent.fullOutput || !!agent.outputPreview;
  const isClickable = hasOutput && onClick;
  const isDone = agent.status === 'complete';
  const isWorking = isActive && agent.status === 'running';

  const displayText = useMemo(() => {
    if (agent.outputPreview) {
      const preview = agent.outputPreview.slice(0, 120);
      return preview + (agent.outputPreview.length > 120 ? '...' : '');
    }
    return null;
  }, [agent.outputPreview]);

  const latency = agent.latencySeconds;

  return (
    <div
      onClick={isClickable ? onClick : undefined}
      className={cn(
        'relative flex items-center gap-3 p-3 rounded-lg border transition-all duration-300',
        'backdrop-blur-sm bg-gradient-to-r',
        isWorking
          ? 'from-emerald-600/15 to-emerald-700/5 border-emerald-600/40 shadow-md shadow-emerald-600/10'
          : isDone
          ? 'from-emerald-600/10 to-emerald-700/5 border-emerald-600/30'
          : 'from-card-idle-from/50 to-card-idle-to/30 border-border/40',
        isClickable && 'cursor-pointer hover:from-card-idle-from/80 hover:to-card-idle-from/60 hover:border-border-light/50 hover:shadow-md'
      )}
      title={isClickable ? 'Click to view full response' : undefined}
    >
      {isWorking && (
        <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-emerald-600/5 to-transparent pointer-events-none" />
      )}
      <div
        className={cn(
          'relative w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0 transition-all duration-300',
          isWorking ? 'bg-emerald-600/25 shadow-inner' : isDone ? 'bg-surface-light/60' : 'bg-surface-light/80'
        )}
        style={{
          backgroundColor: isDone ? `${workstreamColor}20` : undefined
        }}
      >
        {isWorking ? (
          <Loader2
            className="w-4 h-4 animate-spin"
            style={{
              color: workstreamColor,
              filter: `drop-shadow(0 0 6px ${workstreamColor})`
            }}
          />
        ) : (
          <Tooltip content={`AI model: ${provider}`} position="top">
            <span className="transition-all duration-300" style={{ color: workstreamColor }}>
              {getProviderLogo(provider, 16)}
            </span>
          </Tooltip>
        )}
      </div>
      <div className="relative flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className={cn(
            'text-sm font-mono truncate',
            isDone ? 'text-emerald-400' : 'text-text-primary'
          )}>{displayName}</span>
          {latency != null && latency > 0 && (
            <Tooltip content="Processing time in seconds" position="top">
              <span className={cn(
                'text-xs cursor-help',
                isDone ? 'text-emerald-600/70' : 'text-text-muted'
              )}>{latency.toFixed(1)}s</span>
            </Tooltip>
          )}
          {isDone && !isWorking && (
            <span className="text-xs text-verdict-primary font-medium inline-flex items-center gap-0.5 leading-none">
              <span>Done</span>
            </span>
          )}
        </div>
        {displayText && (
          <div className={cn(
            'text-xs line-clamp-2 overflow-hidden mt-0.5',
            isWorking ? 'text-emerald-500/70' : 'text-text-secondary'
          )}>
            <MarkdownContent content={displayText} className="text-xs [&_p]:mb-0 [&_h1]:text-xs [&_h2]:text-xs [&_h3]:text-xs [&_li]:text-xs [&_strong]:text-text-primary" />
            {isWorking && <span className="animate-pulse inline ml-1">|</span>}
          </div>
        )}
        {isWorking && !displayText && (
          <div className="text-xs text-emerald-500/70 font-mono flex items-center gap-1 mt-0.5">
            Researching...
            <span className="animate-pulse">|</span>
          </div>
        )}
      </div>
      {isWorking && (
        <div className="relative w-2.5 h-2.5 flex-shrink-0">
          <div className="absolute inset-0 rounded-full bg-emerald-600 animate-ping opacity-75" />
          <div className="relative w-2.5 h-2.5 rounded-full bg-emerald-500" />
        </div>
      )}
      {isClickable && !isWorking && (
        <ChevronRight className="w-4 h-4 text-text-secondary flex-shrink-0" />
      )}
    </div>
  );
}

// ============================================================================
// WORKSTREAM CARD
// ============================================================================

function WorkstreamCard({ workstream, index, isSmallScreen, onAgentClick, isCurrent }: {
  workstream: StrategicWorkstream;
  index: number;
  isSmallScreen: boolean;
  onAgentClick?: (agent: StrategicAgent) => void;
  isCurrent?: boolean;
}) {
  const [isExpanded, setIsExpanded] = useState(true);
  const color = WORKSTREAM_COLORS[index % WORKSTREAM_COLORS.length];
  const isRunning = workstream.status === 'running' || (isCurrent && workstream.status !== 'complete');
  const isComplete = workstream.status === 'complete';

  return (
    <div className={cn(
      'relative w-full rounded-xl border transition-all duration-500',
      'backdrop-blur-md bg-gradient-to-br overflow-hidden',
      isRunning
        ? 'from-emerald-600/10 to-emerald-700/5 border-emerald-600/40 shadow-lg shadow-emerald-600/10'
        : isComplete
        ? 'from-card-idle-from/50 to-card-idle-to/30 border-verdict-primary/30'
        : 'from-card-idle-from/60 to-card-idle-to/40 border-border/50'
    )}>
      {(isRunning || isComplete) && (
        <div className={cn(
          'absolute inset-0 rounded-xl bg-gradient-to-br pointer-events-none',
          isComplete ? 'from-verdict-primary/5 to-transparent' : 'from-emerald-600/5 to-transparent'
        )} />
      )}

      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={cn(
          "relative w-full flex items-center text-left hover:bg-surface-light transition-colors rounded-t-xl",
          isSmallScreen ? "gap-2.5 p-3" : "gap-3 p-4"
        )}
      >
        <div className={cn(
          'relative rounded-lg flex items-center justify-center font-bold transition-all duration-300',
          isSmallScreen ? 'w-8 h-8 text-xs' : 'w-9 h-9 text-sm',
          isRunning
            ? 'bg-emerald-600/25 text-emerald-500 shadow-inner'
            : isComplete
            ? 'bg-verdict-primary/25 text-verdict-primary'
            : 'bg-surface-light/80 text-text-muted'
        )}>
          {isRunning ? (
            <Loader2 className={cn('animate-spin', isSmallScreen ? 'w-4 h-4' : 'w-4.5 h-4.5')} />
          ) : (
            index + 1
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className={cn(
            "font-semibold text-text-primary flex items-center gap-2",
            isSmallScreen ? "text-sm" : "text-base"
          )}>
            <Zap className={cn("flex-shrink-0", isSmallScreen ? "w-3.5 h-3.5" : "w-4 h-4")} style={{ color: color.text }} />
            <span className="break-words">{workstream.name || `Workstream ${index + 1}`}</span>
          </div>
          <div className={cn(
            "text-text-secondary mt-0.5",
            isSmallScreen ? "text-xs" : "text-sm"
          )}>{workstream.keyQuestions?.[0] || 'Strategic analysis focus'}</div>
        </div>

        <div className={cn("flex items-center", isSmallScreen ? "gap-1.5" : "gap-2")}>
          {isComplete && (
            <Tooltip content="Workstream complete" position="top">
              <div className={cn(
                "rounded-full bg-verdict-primary flex items-center justify-center shadow-lg shadow-verdict-primary/30",
                isSmallScreen ? "w-5 h-5" : "w-6 h-6"
              )}>
                <CheckCircle2 className={cn(isSmallScreen ? "w-3 h-3" : "w-4 h-4", "text-black")} />
              </div>
            </Tooltip>
          )}
          {isExpanded ? (
            <ChevronDown className={cn(isSmallScreen ? "w-4 h-4" : "w-5 h-5", "text-text-secondary")} />
          ) : (
            <ChevronRight className={cn(isSmallScreen ? "w-4 h-4" : "w-5 h-5", "text-text-secondary")} />
          )}
        </div>
      </button>

      {isExpanded && (
        <div className={cn(
          "space-y-2.5 border-t border-border-subtle",
          isSmallScreen ? "px-3 pb-3 pt-3" : "px-4 pb-4 pt-3"
        )}>
          {workstream.agents?.map((agent, agentIdx) => (
            <StrategicAgentNodeCompact
              key={`${workstream.id}-agent-${agentIdx}`}
              agent={agent}
              agentIndex={agentIdx}
              workstreamColor={color.text}
              isActive={!!isRunning}
              onClick={(agent.fullOutput || agent.outputPreview) ? () => onAgentClick?.(agent) : undefined}
              isSmallScreen={isSmallScreen}
            />
          ))}

          {workstream.synthesisPreview && (
            <div className={cn(
              "mt-3 rounded-lg bg-gradient-to-br border p-4",
              "from-amber-500/10 to-orange-500/5 border-amber-500/20"
            )}>
              <div className={cn('flex items-center gap-2 mb-2 text-amber-400', isSmallScreen ? 'text-sm' : 'text-base')}>
                <FileText className="w-4 h-4" />
                <span className="font-semibold">Synthesis</span>
              </div>
              <div className={cn('text-text-primary leading-relaxed', isSmallScreen ? 'text-sm' : 'text-base')}>
                <MarkdownContent content={workstream.synthesisPreview} className={isSmallScreen ? 'text-sm' : 'text-base'} />
              </div>
              {workstream.keyFindings && workstream.keyFindings.length > 0 && (
                <div className="mt-3 pt-3 border-t border-border-subtle">
                  <div className={cn('text-amber-400/80 mb-2 font-medium', isSmallScreen ? 'text-xs' : 'text-sm')}>Key Findings ({workstream.keyFindings.length}):</div>
                  <div className="max-h-[180px] overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-amber-500/20 scrollbar-track-transparent">
                    <ul className={cn('space-y-2', isSmallScreen ? 'text-xs' : 'text-sm')}>
                      {workstream.keyFindings.map((finding, j) => (
                        <li key={j} className="flex items-start gap-2 text-text-primary leading-relaxed">
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-400/60 mt-2 flex-shrink-0" />
                          <MarkdownContent content={finding} className={cn('flex-1 [&_p]:inline', isSmallScreen ? 'text-xs' : 'text-sm')} inline />
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// WORKSTREAMS GRID
// ============================================================================

export function WorkstreamsGrid({ workstreams, phase, isSmallScreen }: {
  workstreams: StrategicWorkstream[]; phase: StrategicPhase; isSmallScreen: boolean;
}) {
  const [selectedAgent, setSelectedAgent] = useState<{ agent: StrategicAgent; workstreamName: string } | null>(null);
  const [isExpandedView, setIsExpandedView] = useState(true);
  const isActive = phase === 'workstreams';
  const isComplete = ['synthesis', 'stress_test', 'debate', 'mediation', 'aggregation', 'concluded'].includes(phase);
  const allComplete = workstreams.every(ws => ws.status === 'complete');

  const handleAgentClick = (agent: StrategicAgent, workstreamName: string) => {
    if (agent.fullOutput || agent.outputPreview) {
      setSelectedAgent({ agent, workstreamName });
    }
  };

  if (workstreams.length === 0 && !isActive && !isComplete) {
    return (
      <div className={cn('mx-auto rounded-xl border border-border/30 bg-surface/50 text-center', isSmallScreen ? 'max-w-[280px] p-3' : 'max-w-[600px] p-4')}>
        <div className={cn('text-text-muted', isSmallScreen ? 'text-xs' : 'text-sm')}>Awaiting workstream decomposition...</div>
      </div>
    );
  }

  return (
    <>
      <div className={cn('mx-auto relative', isSmallScreen ? 'max-w-[320px]' : 'max-w-[1000px]')}>
        <div
          className={cn(
            'absolute -top-3 left-1/2 -translate-x-1/2 z-10 bg-surface border border-amber-500/30 rounded font-mono text-amber-500/70 flex items-center gap-1 whitespace-nowrap',
            isSmallScreen ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-0.5 text-[11px]'
          )}
        >
          <Zap className={cn(isSmallScreen ? 'w-2.5 h-2.5' : 'w-3 h-3')} />
          Running in parallel ({workstreams.length} workstreams)
          {allComplete && (
            <button
              onClick={() => setIsExpandedView(!isExpandedView)}
              className="ml-2 flex items-center gap-1 px-1.5 py-0.5 bg-amber-500/20 hover:bg-amber-500/30 rounded transition-colors"
              title={isExpandedView ? 'Collapse outputs' : 'Expand all outputs'}
            >
              {isExpandedView ? (
                <>
                  <Minimize2 className="w-3 h-3" />
                  Collapse
                </>
              ) : (
                <>
                  <Expand className="w-3 h-3" />
                  Expand All
                </>
              )}
            </button>
          )}
        </div>

        <div
          className={cn(
            'border-2 border-dashed border-amber-500/30 rounded-2xl',
            isSmallScreen ? 'p-3 pt-5' : 'p-4 pt-6'
          )}
        >
          <div className={cn(
            'grid gap-4',
            isSmallScreen
              ? 'grid-cols-1'
              : workstreams.length <= 2
                ? 'grid-cols-2'
                : 'grid-cols-2'
          )}>
            {workstreams.map((ws, i) => (
              <WorkstreamCard
                key={ws.id || i}
                workstream={ws}
                index={i}
                isSmallScreen={isSmallScreen}
                isCurrent={isActive}
                onAgentClick={(agent) => handleAgentClick(agent, ws.name || `Workstream ${i + 1}`)}
              />
            ))}
          </div>
        </div>
      </div>

      {selectedAgent && (
        <StrategicAgentModal
          agent={selectedAgent.agent}
          workstreamName={selectedAgent.workstreamName}
          onClose={() => setSelectedAgent(null)}
        />
      )}
    </>
  );
}
